/**
 * Hell/Dunkel-Schalter: Auto/Hell/Dunkel und der stufenlose Regler.
 *
 * Gemeinsamer Baustein aller Builds (Lead Sheets, Cloud). Speichern
 * uebernimmt der Build: er reicht `save` herein. Jeder Wechsel wird als
 * Ereignis "leadsheet:theme" gemeldet, damit andere Schalter mitziehen.
 */
import { h } from '../ui/dom.ts';
import { AUTO_THEME_ID, THEMES, applyTheme, currentThemeId, themeTone } from './themes.ts';
import { toneThemeId } from './mix.ts';

let save: (themeId: string) => void = () => undefined;

/** Wo der gewaehlte Wert gespeichert wird (einmal beim Start setzen). */
export function setThemeSaver(fn: (themeId: string) => void): void {
  save = fn;
}

/**
 * Weicher Uebergang beim Wechsel zwischen Hell und Dunkel: die Farben
 * gleiten in kleinen Stufen (ein Bild pro Schritt) zum Ziel, statt zu
 * springen. Am Ende steht der gewuenschte Wert, auch "auto".
 */
let glideFrame = 0;
function glideTheme(targetId: string, onStep?: () => void, onDone?: () => void): void {
  cancelAnimationFrame(glideFrame);
  const from = themeTone(currentThemeId());
  const to = themeTone(targetId);
  const start = performance.now();
  const DURATION = 520;
  const step = (now: number): void => {
    const p = Math.min(1, (now - start) / DURATION);
    // Sanft anfahren und sanft auslaufen.
    const eased = p < 0.5 ? 4 * p * p * p : 1 - (-2 * p + 2) ** 3 / 2;
    if (p < 1 && from !== to) {
      applyTheme(toneThemeId(from + (to - from) * eased));
      onStep?.();
      glideFrame = requestAnimationFrame(step);
    } else {
      applyTheme(targetId);
      onStep?.();
      onDone?.();
    }
  };
  glideFrame = requestAnimationFrame(step);
}

/**
 * Schalter für das Design: Auto/Hell/Dunkel und der stufenlose Regler.
 * Steht in Einstellungen → Feel.
 */
export function themeControls(): HTMLElement {
  const options = [{ id: AUTO_THEME_ID, label: 'Auto' }, ...THEMES.map((t) => ({ id: t.id, label: t.name }))];

  const group = h('div', { class: 'segmented', attrs: { role: 'group', 'aria-label': 'Hell und Dunkel' } });
  const buttons = options.map((option) => {
    const el = h('button', {
      class: 'segmented__item',
      text: option.label,
      attrs: { type: 'button' },
      on: {
        click: () => {
          save(option.id);
          // Erst am Ende melden: sonst lesen andere Schalter (und main.ts
          // fuer den Automatik-Modus) noch den alten Stand.
          glideTheme(option.id, mark, () =>
            window.dispatchEvent(new CustomEvent<string>('leadsheet:theme', { detail: option.id })),
          );
        },
      },
    });
    group.append(el);
    return { id: option.id, el };
  });

  // Auch mitziehen, wenn das Design anderswo (Einstellungen) gewechselt wird.
  // Die Leiste wird bei jedem Seitenwechsel neu gebaut: eine abgeloeste
  // Fassung meldet sich deshalb beim naechsten Mal selbst ab.
  const onTheme = (): void => {
    if (group.isConnected) mark();
    else window.removeEventListener('leadsheet:theme', onTheme);
  };
  window.addEventListener('leadsheet:theme', onTheme);

  /*
   * Stufenloser Regler darunter: von Hell ueber viele Zwischenstufen bis
   * Dunkel. Waehrend des Ziehens folgen die Farben direkt dem Finger
   * (einmal pro Bild), gespeichert wird erst beim Loslassen.
   */
  const slider = h('input', {
    class: 'tone-slider__input',
    attrs: { type: 'range', min: '0', max: '100', step: '1', 'aria-label': 'Helligkeit von Hell bis Dunkel' },
  });
  let frame = 0;
  slider.addEventListener('input', () => {
    cancelAnimationFrame(glideFrame);
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      applyTheme(toneThemeId(Number(slider.value) / 100));
      mark(false);
    });
  });
  slider.addEventListener('change', () => {
    cancelAnimationFrame(frame);
    const id = toneThemeId(Number(slider.value) / 100);
    applyTheme(id);
    save(id);
    window.dispatchEvent(new CustomEvent<string>('leadsheet:theme', { detail: id }));
    mark();
  });
  const sliderBox = h(
    'div',
    { class: 'tone-slider' },
    slider,
    h(
      'div',
      { class: 'tone-slider__labels' },
      h('span', { text: 'Hell' }),
      h('span', { text: 'Dunkel' }),
    ),
  );

  function mark(moveSlider = true): void {
    const current = currentThemeId();
    const tone = themeTone(current);
    for (const entry of buttons) {
      // Hell/Dunkel leuchten nur an den Enden, Auto nur im Automatik-Modus.
      const on = entry.id === AUTO_THEME_ID ? current === AUTO_THEME_ID : current !== AUTO_THEME_ID && themeTone(entry.id) === tone;
      entry.el.classList.toggle('segmented__item--on', on);
      entry.el.setAttribute('aria-pressed', String(on));
    }
    if (moveSlider) slider.value = String(Math.round(tone * 100));
  }
  mark();

  return h('div', { class: 'theme-controls' }, group, sliderBox);
}

